@import './fluid-context-menu.theme.less';
@import '../../styles/dropdown-menu.styles.less';

@import '../../styles/mixins/fluid-fading-animations.mixin.less';
@import '../../styles/mixins/fluid-transitions.mixin.less';
@import '../../styles/mixins/fluid-common-components.mixin.less';

// ================================================================ //
// -- Theme Definition

:host {
  display: inline-block;

  *, ::after, ::before {
    box-sizing: border-box;
  }

  .fluid-context-menu {

    /**
     * LEGACY THEME
     */
    &.legacy {
      @import '../../styles/variables-iuw-legacy.less';
      @context-menu-tokens: @context-menu-tokens-legacy;
      @dropdown-menu();
      @context-menu();
    }

    /**
     * LIBERTY MUTUAL THEME
     */
    &.lm {
      @import '../../styles/variables-lm.less';
      @context-menu-tokens: @context-menu-tokens-lm;
      @dropdown-menu();
      @context-menu();
    }

    /**
     * LM CORPORATE THEME
     */
    &.corp, &.default {
      @import '../../styles/variables-lm-corp.less';
      @context-menu-tokens: @context-menu-tokens-corp;
      @dropdown-menu();
      @context-menu();
    }

    /**
     * PORTAL THEME
     */
    &.portal {
      @import '../../styles/variables-lm-portal.less';
      @context-menu-tokens: @context-menu-tokens-portal;
      @dropdown-menu();
      @context-menu();
    }
  }

  // == Block Layout

  &.fluid-context-menu--block {
    display: block;
  }
}


// ================================================================ //
// -- Component Detached Ruleset

@context-menu: {
  @menu: @context-menu-tokens[@menu];
  @menu-actions: @context-menu-tokens[@menu-actions];
  @inline-menu-modifier: @context-menu-tokens[@inline-menu-modifier];

  @link-icon-height: if(
    (isunit(@menu-item-line-height, px)),
    @menu-item-line-height,
    (@context-menu-tokens[font-size] * @menu-item-line-height)
  );

  display: flex;
  align-items: center;
  position: relative;
  font-family: @font-family;
  font-size: @context-menu-tokens[font-size];
  font-weight: @context-menu-tokens[font-weight];
  color: @context-menu-tokens[text-color];

  // == Block Layout

  &.block-layout {
    .menu-trigger {
      flex: 1 1 100%;
      display: block;
    }

    .fluid-dropdown-menu {
      width: auto;
      min-width: unset;
      max-width: 100%;
    }
  }

  // == Disabled State

  &.disabled {
    cursor: not-allowed;
  }

  // ==== Trigger Element

  .menu-trigger {
    @button-reset();
    display: inline-flex;
    justify-content: center;
    align-items: center;

    @media @screen-ie-only {
      overflow: visible; // Prevents IE11 box-shadow truncation for LM Corp theme
    }

    // == Native Clickable Trigger Element

    &.as-wrapper {
      &:hover,
      &:focus {
        outline: 0;
      }

      &.disabled {
        pointer-events: none;

        ::slotted(*) {
          pointer-events: none;
        }
      }
    }

    // == Expand Clickable Target

    &:not(.as-wrapper) {
      outline: 0;

      &::before {
        display: block;
        position: absolute;
        top: -0.5em;
        bottom: -0.5em;
        left: -0.5em;
        right: -0.5em;
        content: "";
      }

      &:focus-visible {
        &::before {
          @trigger-pseudo-outline();
        }
      }
    }

    // == Disabled Trigger

    &.disabled {
      opacity: .5;
      cursor: not-allowed;
    }
  }

  // ==== Menu Container
  // NOTE: Base dropdown menu styles in "dropdown-menu.styles.less"

  .fluid-dropdown-menu {
    width: max-content;
    min-width: @menu[min-width];
    max-width: @menu[max-width];
    padding-left: @menu-item-padding-left;
    padding-right: @menu-item-padding-right;
    will-change: transform;
    contain: layout;
    outline: none;

    // == IE11 Fallback

    @media @screen-ie-only {
      width: auto;
      white-space: nowrap; // Alternative to "width: max-content"
    }

    // == Expand Component Boundaries

    @menu-pseudo-border();

    // == Inline Sections

    &.inline-sections {
      display: flex;
      max-width: none;
      padding-left: @menu-item-padding-left * @inline-menu-modifier;
      padding-right: @menu-item-padding-right * @inline-menu-modifier;
    }

    // == Remove Menu from DOM

    &.menu-hidden {
      display: none;
    }
  }

  // ==== Menu Section

  .menu-section {
    @media @screen-ie-only {
      white-space: normal; // Alternative to "width: max-content"
    }
  }

  .inline-sections {
    .menu-section {
      min-width: @menu[min-width] / 2;
    }
  }

  // ==== Menu Title

  .menu-title {
    display: block;
    font-size: @menu-title-font-size;
    font-weight: @menu-title-font-weight;
    text-align: left;
    margin-top: @menu-title-margin-top;
    margin-bottom: @menu-title-margin-bottom;
  }

  // ==== Menu Unordered List

  .menu-list {
    list-style: none;
    padding: 0;
    margin: 0;
    margin-left: -@menu-item-padding-left;
    margin-right: -@menu-item-padding-right;
    overscroll-behavior-y: contain;
  }

  // ==== Menu Slot

  .menu-slot {
    overscroll-behavior-y: contain;

    &.offset-padding {
      margin-left: -@menu-item-padding-left;
      margin-right: -@menu-item-padding-right;
    }
  }

  // ==== Menu List Item

  .menu-item {
    display: block;
    line-height: @menu-item-line-height;
  }

  // ==== Menu Item Link & Toggle (Shared)

  .menu-item-link,
  .menu-item-toggle {
    appearance: none;
    text-align: left;
    color: inherit;
    font-size: 1em;
    cursor: pointer;
  }

  // ==== Menu Item Link

  .menu-item-link {
    @button-reset();
    width: 100%;
    display: flex;
    align-items: center;
    padding-top: @menu-item-padding-top;
    padding-bottom: @menu-item-padding-bottom;
    padding-left: @menu-item-padding-left;
    padding-right: @menu-item-padding-right;
    text-decoration: none;

    .transition(@property: background-color; @duration: short);

    // == Hover & Focus-Visible

    &:hover,
    &:focus-visible {
      background-color: @menu-item-hover-background;
      outline: 0;
    }

    // == Link Text

    .link-label {
      font-size: inherit;
      line-height: inherit;
    }

    // == Link Icon

    .link-icon {
      height: @link-icon-height;
      display: flex;
      align-items: center;

      fluid-icon {
        padding-bottom: 0.5px; // Fixes 0.5px vertical alignment offset
      }

      &.icon-before {
        padding-right: 0.5em;
      }

      &.icon-after {
        padding-left: 0.5em;
        margin-left: auto;
      }
    }
  }

  // ==== Menu Item Toggle

  .menu-item-toggle {}

  // ==== Menu Item Toggle Group

  .menu-toggle-group {
    overflow-y: auto;
    margin-left: -@menu-item-padding-left;
    margin-right: -@menu-item-padding-right;
    overscroll-behavior-y: contain;

    &:not(.section-height-applied) {
      max-height: 35vh;
    }
  }

  // ==== Divider

  .menu-divider {
    margin-top: @menu-divider-margin-top;
    margin-bottom: @menu-divider-margin-bottom;
    margin-left: 0;
    margin-right: 0;
    border-width: 0;
    border-color: @menu-divider-border-color;
    border-style: @border-style;
    border-top-width: @border-width;
  }

  // == Inline Sections Divider

  .inline-sections {
    .menu-divider {
      margin-top: 0;
      margin-bottom: 0;
      margin-left: @menu-item-padding-left * @inline-menu-modifier;
      margin-right: @menu-item-padding-right * @inline-menu-modifier;
      border-width: 0;
      border-left-width: @border-width;
    }
  }

  // ==== Menu Actions

  .menu-actions {
    text-align: right;
    margin-top: @menu-actions[margin-top];

    &.compact {
      position: absolute;
      top: 0;
      right: 0;
      margin: 0;
      z-index: 3;
    }

    .close-button {
      .close-button();
    }
  }
};


// ================================================================ //
// -- Utility Detached Ruleset(s)

@button-reset: {
  background-color: transparent;
  font-family: inherit;
  font-size: inherit;
  line-height: inherit;
  margin: 0;
  padding: 0;
  border: none;
  color: inherit;
  cursor: pointer;
};

@menu-pseudo-border: {
  &::before {
    display: block;
    position: absolute;
    top: -8px; // PopperJS offset "distance" (i.e. [skidding, distance])
    bottom: -1em;
    left: -1em;
    right: -1em;
    content: "";
    z-index: -1;
  }
}

@trigger-pseudo-outline: {
  outline-style: auto;
  outline-width: 2px;
  outline-offset: 0;
  outline-color: Highlight;

  @media (-webkit-min-device-pixel-ratio: 0) {
    outline-color: -webkit-focus-ring-color;
  }
}
